import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Accordion, Card, Col, Form, Modal, Row, Tab, Tabs } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { buildAlert } from '../../../../actions/index.js'
import { checkPersonNumber } from '../../../../actions/persons/personnumbercheck.js'
import { getPersonNumber } from '../../../../actions/persons/personnumberget.js'
import { postPerson } from '../../../../actions/persons/personpost.js'
import { checkPersonSocSec } from '../../../../actions/persons/personsocseccheck.js'
import { determineIdentifierCodeType } from '../../../../actions/validation.js'
import {
	employmentHistoryGetByPerson,
	procountorVersionCheck,
	salaryHistoryGetByPerson,
} from '../../../../api/index.js'
import { AttachmentView } from '../../../../common/attachments/attachmentview.jsx'
import { Button } from '../../../../common/Button.tsx'
import { DuplicateSocSecModal } from '../../../../common/DuplicateSocSecModal.jsx'
import { IntegrationWarningModal } from '../../../../common/IntegrationWarningModal.jsx'
import { IconAddNew, TemplateProfileImage } from '../../../../common/icons.jsx'
import { validateActiveIntegrations } from '../../../../common/integrationValidation.jsx'
import { LoadingButton } from '../../../../common/loaders/loadingbutton.jsx'
import { ProcountorVersionWarningModal } from '../../../../common/ProcountorVersionWarningModal.jsx'
import { fieldParams } from '../../../../common/person/personmodalfieldparams.js'
import { findPersonModalFormErrors } from '../../../../common/person/personmodalvalidation.js'
import { eSalaryImage, formBIC, getCountryOptions } from '../../../../common/utils.jsx'
import {
	actionTypeSelector,
	activeIntegrationsSelector,
	authSelector,
	personNumberCheckSelector,
	socSecCheckSelector,
} from '../../../../reducers/index.js'
import CourseView from '../../../competences/courses/index.jsx'
import EducationView from '../../../competences/educations/index.jsx'
import LanguagesView from '../../../competences/languages/index.jsx'
import SkillsView from '../../../competences/skills/index.jsx'
import { calcTrialPeriodEndDate } from '../../../contracts/modal/contractreducer.js'
import DiscussionView from '../../../discussions/index.jsx'
import EmploymentEndBanner from '../EmploymentEndBanner.jsx'
import { EmploymentInfo } from '../tabs/employmentinfo.jsx'
import { InternationalSituations } from '../tabs/internationalsituations.jsx'
import { OrganizationalData } from '../tabs/organizationaldata.jsx'
import { PersonAbsences } from '../tabs/personabsences.jsx'
import { PersonalDetails } from '../tabs/personaldetails.jsx'
import { PersonalMemos } from '../tabs/personalmemos.jsx'
import { PersonSettings } from '../tabs/personsettings.jsx'
import { SalaryInformation } from '../tabs/salary/salaryinformation.jsx'

const emptyPerson = {
	personId: null,
	nr: null,
	gender: 'M',
	details: {},
	controlDetails: { language: 'fi', payslipMethod: 'XML' }, // i18n.resolvedLanguage
	nationalDetails: {},
	salaryDetails: { carUsageBenefit: true },
	employmentDetails: { personStatus: 'EMPLOYED' },
	otherDetails: {},
	settings: {},
}

// personData contains a single person's data from API
export default function PersonModal({ show, setShow, personData }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const isPersonal = window.location.pathname.startsWith('/personal')

	const formMethods = useForm({ defaultValues: emptyPerson })
	const { reset } = formMethods

	const auth = useSelector(authSelector)
	const isBasicLevel = auth.hrLevel !== 'premium'

	const actionType = useSelector(actionTypeSelector)
	const personNumberCheckResult = useSelector(personNumberCheckSelector)
	const socSecCheckResult = useSelector(socSecCheckSelector)

	const [countryOptions, setCountryOptions] = useState([])

	// from validation errors
	const [formErrors, setFormErrors] = useState({})
	const [submitForm, setSubmitForm] = useState(false)
	const [socSecDuplicateWarning, setSocSecDuplicateWarning] = useState(false)

	const fieldTypingTimers = useMemo(() => ({}), [])
	const typingDelay = 200

	// includes details, controlDetails, nationalDetails, salaryDetails, otherDetails, dimensions
	const [person, setPerson] = useState(personData?.personId ? personData : emptyPerson)

	// Prevents stale personData (Redux never clears it) from overwriting emptyPerson when
	const isNewPersonIntent = useRef(false)

	useEffect(() => {
		if (show && personData?.personId && !isNewPersonIntent.current) {
			// Opening an existing person from the grid
			setPerson(personData)
			reset(personData)
		} else if (!show) {
			// Modal closed — clear flag so next grid-open syncs normally
			isNewPersonIntent.current = false
			setPerson(emptyPerson)
		}
	}, [show, personData, reset])

	const [activeTab, setActiveTab] = useState('person')
	const selectTab = (eventKey) => {
		/*
        if (isBasicLevel && eventKey === "attachments") {
          dispatch(localized.premiumAlert);
        } else {
    */
		setActiveTab(eventKey)
		//    }
	}

	const handleClose = () => {
		dispatch({ type: 'GET_PERSON_CANCELLED' })
		setShow(false)
		reset(emptyPerson)
		setPerson(emptyPerson)
		dimensionsDraftRef.current = []
		setFormErrors({})
		setSubmitForm(false)
		setSocSecDuplicateWarning(false)
		setEmploymentEnd(false)
		setActiveTab('person')
	}

	const [employmentEnd, setEmploymentEnd] = useState(false)
	const handleEmploymentEnd = () => {
		setEmploymentEnd(true)
		setActiveTab('controlDetails')
	}

	const handleEmploymentOpen = () => {
		setEmploymentEnd(false)
		setPerson({
			...person,
			employmentDetails: {
				...person.employmentDetails,
				endDate: null,
				endInfo: '',
				endCode: null,
			},
		})
		setActiveTab('controlDetails')
	}

	const handleShow = () => {
		isNewPersonIntent.current = true
		reset(emptyPerson)
		setPerson(emptyPerson)
		dimensionsDraftRef.current = []
		setShow(true)
	}

	const executeFinalSave = useCallback(() => {
		setSubmitForm(true)
		dispatch(postPerson(person))
		setShowIntegrationConfirm(false)
	}, [dispatch, person])

	const activeIntegrations = useSelector(activeIntegrationsSelector) || {}
	const runIntegrationCompletenessCheck = useCallback(() => {
		const errors = validateActiveIntegrations(person, activeIntegrations)
		if (errors.length > 0) {
			setIntegrationErrors(errors)
			setShowIntegrationConfirm(true)
			setSubmitForm(false)
		} else {
			executeFinalSave()
		}
	}, [executeFinalSave, person, activeIntegrations])

	const proceedToSave = useCallback(async () => {
		if (person.personId === null) {
			delete person.personId
		}

		const isEdit = !!person.personId
		const procountorActive = !!activeIntegrations.tokenProcountor

		if (isEdit && procountorActive) {
			try {
				const { data } = await procountorVersionCheck(person.personId)
				if (data?.changedInProcountor) {
					setShowProcountorVersionWarning(true)
					setSubmitForm(false)
					return
				}
			} catch (e) {
				console.warn('Procountor version check failed', e)
			}
		}

		runIntegrationCompletenessCheck()
	}, [person, activeIntegrations.tokenProcountor, runIntegrationCompletenessCheck])

	const handleSubmitFinal = useCallback(
		(nrResult) => {
			const newErrors = findPersonModalFormErrors(t, null, null, person)

			if (nrResult?.exists) {
				if (!person.personId || (person.personId && person.personId !== personNumberCheckResult.existsPersonId)) {
					newErrors.nr = t('PersonNumberAlreadyExists', 'Henkilönumero on jo käytössä. Seuraava vapaa on {{nextNr}}', {
						nextNr: personNumberCheckResult.nextNr,
					})
				}
			}

			if (Object.keys(newErrors).length > 0) {
				setSubmitForm(false)
				setFormErrors(newErrors)
				dispatch(
					buildAlert(
						'ERROR',
						t('FormContainsErrors', 'Lomakkeella on virheitä'),
						t('PleaseFixFormErrors', 'Ole hyvä ja korjaa virheet'),
						Object.values(newErrors),
					),
				)
				return
			}

			// For new persons with a socSec, check for duplicates before saving
			if (!person.personId && person.socSec) {
				dispatch(checkPersonSocSec(person.socSec))
				return // wait for CHECK_SOC_SEC_SUCCEEDED
			}

			proceedToSave()
		},
		[person, personNumberCheckResult, proceedToSave, t, dispatch],
	)

	useEffect(() => {
		if (show) {
			getCountryOptions(setCountryOptions)
			if (!personData?.personId || isNewPersonIntent.current) {
				// new person
				dispatch(getPersonNumber())
			}
		} else {
			Object.keys(fieldTypingTimers).forEach((key) => {
				clearTimeout(fieldTypingTimers[key])
			})
		}
	}, [show, dispatch, fieldTypingTimers, personData?.personId])

	const [employmentHistory, setEmploymentHistory] = useState([])

	useEffect(() => {
		if (show && person?.personId) {
			const fetchEmploymentHistory = async () => {
				try {
					const history = await employmentHistoryGetByPerson(person.personId)
					setEmploymentHistory(history.employmentPeriods ?? [])
				} catch (error) {
					console.error('Employment history fetch error: ', error)
				}
			}
			fetchEmploymentHistory()
		}
	}, [show, person?.personId])

	const [salaryHistory, setSalaryHistory] = useState([])

	useEffect(() => {
		if (show && person?.personId) {
			const fetchSalaryHistory = async () => {
				try {
					const result = await salaryHistoryGetByPerson(person.personId)
					if (!result || !Array.isArray(result)) {
						setSalaryHistory([])
					} else {
						setSalaryHistory(result.map((item) => ({ ...item, nr: person.nr })))
					}
				} catch (error) {
					console.error('Salary history fetch error:', error)
					setSalaryHistory([])
				}
			}
			fetchSalaryHistory()
		}
	}, [show, person?.personId, person.nr])

	useEffect(() => {
		if (actionType === 'CHECK_PERSON_NUMBER_SUCCEEDED') {
			if (submitForm) {
				handleSubmitFinal(personNumberCheckResult)
			} else {
				if (personNumberCheckResult?.exists) {
					if (!person.personId || (person.personId && person.personId !== personNumberCheckResult.existsPersonId)) {
						setFormErrors({
							...formErrors,
							nr: t('PersonNumberAlreadyExists', 'Henkilönumero on jo käytössä. Seuraava vapaa on {{nextNr}}', {
								nextNr: personNumberCheckResult.nextNr,
							}),
						})
					}
				}
			}
		} else if (actionType === 'CHECK_SOC_SEC_SUCCEEDED') {
			if (submitForm) {
				if (socSecCheckResult?.exists && !socSecDuplicateWarning) {
					setSocSecDuplicateWarning(true)
					setSubmitForm(false)
				} else {
					setSocSecDuplicateWarning(false)
					proceedToSave()
				}
			}
		} else if (
			actionType === 'POST_PERSON_SUCCEEDED' ||
			actionType === 'POST_PERSON_EDIT_SUCCEEDED' ||
			actionType.endsWith('_FAIL') ||
			actionType === 'ALERT'
		) {
			setSubmitForm(false)
			if (actionType === 'POST_PERSON_SUCCEEDED' || actionType === 'POST_PERSON_EDIT_SUCCEEDED') {
				if (isPersonal) setShow(false) // close modal on personal page
				setEmploymentEnd(false)
				setActiveTab('person')
			}
		}
	}, [
		actionType,
		handleSubmitFinal,
		personNumberCheckResult,
		person,
		formErrors,
		t,
		submitForm,
		socSecCheckResult,
		socSecDuplicateWarning,
		proceedToSave,
		isPersonal,
		setShow,
	])

	// state is collected from input id and value attributes
	const handlePerson = (e) => {
		const id = e.target.id
		const value = e.target.value

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			setPerson({
				...person,
				[id]: value,
			})
			handleValidation(e)
		}, typingDelay)
	}

	const handleDetails = (e) => {
		const id = e.target.id
		const value = e.target.value

		// Check if timer exists for field and clear it if it does
		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		// Create a timer to process user input only after user has stopped typing
		fieldTypingTimers[id] = setTimeout(() => {
			setPerson((currentPerson) => ({
				...currentPerson,
				details: {
					...currentPerson.details,
					[id]: value,
				},
			}))
			handleValidation(e)
		}, typingDelay)
	}

	const clampPercent = (v) => {
		const n = Number(v)
		if (!Number.isFinite(n)) return 0
		return Math.max(0, Math.min(100, n))
	}

	// DimensionAllocations -> DTO items
	const mapAllocToItems = (allocs) =>
		(allocs || [])
			.filter((a) => a && a.id != null)
			.map((a) => ({
				codeKey: a.id,
				codeValue: a.codeValue,
				percent: clampPercent(a.percent),
			}))

	// insert/update/delete dimension in dimension array
	const upsertDimension = (prev, codeKey, items, codeValue) => {
		const out = Array.isArray(prev) ? prev.slice() : []
		const idx = out.findIndex((d) => d.codeKey === codeKey)

		if (!items || items.length === 0) {
			if (idx !== -1) out.splice(idx, 1) // delete dimension if it is empty
			return out
		}

		const nextDim = { codeKey, codeValue, items }
		if (codeValue != null) nextDim.codeValue = codeValue

		if (idx === -1) out.push(nextDim)
		else out[idx] = nextDim

		return out
	}

	// Accumulates dimension changes across all fields so that editing one dimension
	// does not erase the others. A ref is used because the draft is never rendered.
	const dimensionsDraftRef = useRef(Array.isArray(person?.dimensions) ? [...person.dimensions] : [])

	// Re-sync when fresh person data arrives from the API (e.g. after page refresh
	// and modal reopen) so that the draft always starts from the latest saved state.
	useEffect(() => {
		if (personData?.personId) {
			dimensionsDraftRef.current = Array.isArray(personData?.dimensions) ? [...personData.dimensions] : []
		}
	}, [personData])

	const handleDimensionAllocChange = (codeKey, codeValue) => (allocs) => {
		const items = mapAllocToItems(allocs)
		const next = upsertDimension(dimensionsDraftRef.current, codeKey, items, codeValue)
		dimensionsDraftRef.current = next
		handleDimensions?.({
			target: {
				id: 'dimensions',
				value: next,
			},
		})
	}

	const handleDimensions = (e) => {
		const id = e.target.id
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			setPerson((prev) => ({
				...prev,
				[id]: value,
			}))
		}, typingDelay)
	}

	const handleControlDetails = (e) => {
		const id = e.target.id
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			setPerson({
				...person,
				controlDetails: {
					...person.controlDetails,
					[id]: value,
				},
			})
		}, typingDelay)
	}

	const handleNationalDetails = (e) => {
		const id = e.target.id
		const value = e.target.value

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			setPerson({
				...person,
				nationalDetails: {
					...person.nationalDetails,
					[id]: value,
				},
			})
		}, typingDelay)
	}

	const handleSalaryDetails = (e) => {
		let { id } = e.target
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
		if (e.target.type === 'radio' && e.target.name.length > 0) {
			id = e.target.name
		}

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			let paymentPercentageSalary // calculate monthly wage by paymentPercentage
			if (id === 'monthlyWage' && value && person.salaryDetails.paymentPercentage) {
				paymentPercentageSalary = (value * person.salaryDetails.paymentPercentage) / 100
			} else if (id === 'paymentPercentage' && value && person.salaryDetails.monthlyWage) {
				paymentPercentageSalary = (value / 100) * person.salaryDetails.monthlyWage
			}

			const updatedSalaryDetails = {
				...person.salaryDetails,
				paymentPercentageSalary: paymentPercentageSalary,
				[id]: id === 'partTimePercentage' && value === '' ? null : value,
			}

			if (id === 'employmentType' && value !== '2') {
				updatedSalaryDetails.partTimePercentage = null
			}

			setPerson({
				...person,
				salaryDetails: updatedSalaryDetails,
			})
		}, typingDelay)
	}

	const handleEmploymentDetails = (e) => {
		let { id } = e.target
		let value = e.target.type === 'checkbox' || e.target.type === 'radio' ? e.target.checked : e.target.value

		// radio buttons
		if (id === 'fixedDuration.false') {
			id = 'fixedDuration'
			value = false
		} else if (id === 'fixedDuration.true') {
			id = 'fixedDuration'
			value = true
		}

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			if (id === 'professionCodeAccidentInsurance') {
				value = value.trim() // whitespaces not allowed in Tulorekisteri
			}

			const newEmploymentDetails = {
				...person.employmentDetails,
				[id]: value,
			}

			if (id === 'trialPeriodLength' || id === 'startDate' || id === 'trialPeriodLengthDays') {
				const trialPeriodEndDate = calcTrialPeriodEndDate(
					newEmploymentDetails.startDate,
					newEmploymentDetails.trialPeriodLength,
					newEmploymentDetails.trialPeriodLengthDays,
				)
				if (trialPeriodEndDate) newEmploymentDetails.trialPeriodEndDate = trialPeriodEndDate
			}

			setPerson({
				...person,
				employmentDetails: newEmploymentDetails,
			})
		}, typingDelay)
	}

	const handleOtherDetails = (e) => {
		const id = e.target.id
		const value = e.target.value

		if (fieldTypingTimers[id]) {
			clearTimeout(fieldTypingTimers[id])
		}

		fieldTypingTimers[id] = setTimeout(() => {
			handleValidation(e)
			setPerson({
				...person,
				otherDetails: {
					...person.otherDetails,
					[id]: value,
				},
			})
		}, typingDelay)
	}

	const handleValidation = (e) => {
		const newErrors = findPersonModalFormErrors(t, e, formErrors, person)

		if (e.target.id === 'iban' && newErrors.iban === undefined) {
			setPerson((currentPerson) => {
				// set state
				const newBic = formBIC(e.target.value)
				const newPerson = {
					...currentPerson,
					details: {
						...currentPerson.details,
					},
				}
				if (newBic) {
					newPerson.details.bic = newBic
					formMethods.setValue('details.bic', newBic) // set uncontrolled input value
				}
				return newPerson
			})
		} else if (e.target.id === 'socSec') {
			setSocSecDuplicateWarning(false)
			if (newErrors.socSec === undefined) {
				const newGender = determineIdentifierCodeType(e.target.value)
				if (newGender) {
					setPerson((currentPerson) => ({
						...currentPerson,
						gender: newGender,
					}))
					formMethods.setValue('gender', newGender)
				}
			}
		}

		setFormErrors(newErrors)
		if (
			e?.target &&
			e.target.id === 'nr' &&
			e.target.value > 0 &&
			e.target.value.length < 20 &&
			e.target.value !== ''
		) {
			dispatch(checkPersonNumber(e.target.value))
		}
		return newErrors
	}

	const handleSubmit = () => {
		setSubmitForm(true)

		// wait for pending typing timers to flush state before submitting
		setTimeout(() => {
			if (person.nr > 0 && person.nr !== '') {
				dispatch(checkPersonNumber(person.nr))
			} else {
				handleSubmitFinal()
			}
		}, typingDelay + 50)
	}

	const [showIntegrationConfirm, setShowIntegrationConfirm] = useState(false)
	const [integrationErrors, setIntegrationErrors] = useState([])
	const [showProcountorVersionWarning, setShowProcountorVersionWarning] = useState(false)

	const continueAfterProcountorVersionCheck = () => {
		setShowProcountorVersionWarning(false)
		runIntegrationCompletenessCheck()
	}

	const selectChange = (newValue, actionMeta) => {
		const { name } = actionMeta
		const value = newValue === null ? '' : newValue.value // actionMeta.action === "clear"

		switch (actionMeta.name) {
			// personData.details
			case 'country':
				setPerson({
					...person,
					details: {
						...person.details,
						[name]: value,
					},
				})
				break

			// personData.nationalDetails
			case 'countryCode':
			case 'nationality':
			case 'nationalitySecondary':
				setPerson({
					...person,
					nationalDetails: {
						...person.nationalDetails,
						[name]: value,
					},
				})
				break

			// personData.controlDetails
			case 'language':
			case 'dimension6':
				setPerson({
					...person,
					controlDetails: {
						...person.controlDetails,
						[name]: value,
					},
				})
				break
		}
	}

	const handleCodeItem = (newValue, actionMeta) => {
		const codeItemId = newValue ? newValue.value : null

		setPerson({
			...person,
			controlDetails: {
				...person.controlDetails,
				[actionMeta.name]: { codeItemId },
			},
		})
	}

	const handleEmploymentCodeItem = (newValue, actionMeta) => {
		const codeItemId = newValue ? newValue.value : null

		setPerson({
			...person,
			employmentDetails: {
				...person.employmentDetails,
				[actionMeta.name]: codeItemId,
			},
		})
	}

	const [loadCompetences, setLoadCompetences] = useState(false)
	const [loadDiscussions, setLoadDiscussions] = useState(false)

	const internationalSituationsInfo = (
		<Card className="mb-3 infoBanner">
			<Card.Body>
				{' '}
				{t(
					'internationalSituationsInfoText',
					'Tämän välilehden tiedot täydennetään vain kansainvälisissä tilanteissa tulorekisterin palkkatietoilmoitusta varten. Tarkenna tarvittaessa palkanlaskennasta tietovaatimukset.',
				)}
			</Card.Body>
		</Card>
	)

	return (
		<>
			<Button testId="add-person" className="addbutton" onClick={handleShow} variant="primary">
				<IconAddNew /> {t('AddPerson', 'Lisää henkilö')}
			</Button>
			{show && (
				<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
					<Modal.Header closeButton={!submitForm}>
						{person.otherDetails?.image ? (
							<img
								src={eSalaryImage(auth.eSalaryCompanyId, person.otherDetails.image)}
								crossOrigin="anonymous"
								aria-hidden
							/>
						) : (
							<TemplateProfileImage />
						)}
						<Modal.Title style={{ width: '100%' }}>
							{person.personId ? person.name : t('AddPerson', 'Lisää henkilö')}
						</Modal.Title>
					</Modal.Header>

					<FormProvider {...formMethods}>
						<Modal.Body className="d-flex flex-column p-0" as="form">
							<Tabs onSelect={selectTab} activeKey={activeTab} defaultActiveKey="person" className="scrollable mb-3">
								{/* mountOnEnter */}

								<Tab eventKey="person" title={t('PersonalDetails', 'Henkilötiedot')}>
									<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
									<PersonalDetails
										person={person}
										setPerson={setPerson}
										handlePerson={handlePerson}
										handleDetails={handleDetails}
										handleOtherDetails={handleOtherDetails}
										handleControlDetails={handleControlDetails}
										selectChange={selectChange}
										countryOptions={countryOptions}
										fieldParams={fieldParams}
										formErrors={formErrors}
										isPersonal={isPersonal}
									/>
								</Tab>

								<Tab eventKey="organizationalData" title={t('OrganizationalData', 'Organisaatiotiedot')}>
									<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
									<OrganizationalData
										person={person}
										setPerson={setPerson}
										selectChange={selectChange}
										handleControlDetails={handleControlDetails}
										handleCodeItem={handleCodeItem}
										fieldParams={fieldParams}
										formErrors={formErrors}
										isPersonal={isPersonal}
										handleDimensionAllocChange={handleDimensionAllocChange}
									/>
								</Tab>

								<Tab eventKey="controlDetails" title={t('EmploymentInfo', 'Työsuhdetiedot')}>
									<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
									<EmploymentInfo
										person={person}
										handleEmploymentDetails={handleEmploymentDetails}
										handleEmploymentCodeItem={handleEmploymentCodeItem}
										fieldParams={fieldParams}
										formErrors={formErrors}
										employmentEnd={employmentEnd}
										isPersonal={isPersonal}
										employmentHistory={employmentHistory}
									/>
								</Tab>

								<Tab eventKey="nationalDetails" title={t('InternationalSituations', 'Kv-tilanteet')}>
									<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
									{internationalSituationsInfo}
									<InternationalSituations
										person={person}
										handleNationalDetails={handleNationalDetails}
										selectChange={selectChange}
										countryOptions={countryOptions}
										fieldParams={fieldParams}
										formErrors={formErrors}
										isPersonal={isPersonal}
									/>
								</Tab>

								{(auth.salary || isPersonal) && (
									<Tab eventKey="salaryDetails" title={t('SalaryInformation', 'Palkkatiedot')}>
										<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
										<SalaryInformation
											person={person}
											setPerson={setPerson}
											handleSalaryDetails={handleSalaryDetails}
											fieldParams={fieldParams}
											formErrors={formErrors}
											isPersonal={isPersonal}
											salaryHistory={salaryHistory}
										/>
									</Tab>
								)}

								<Tab eventKey="absences" title={t('Absences', 'Poissaolot')}>
									<PersonAbsences person={person} isPersonal={isPersonal} />
								</Tab>

								{person.personId && auth.knowledge && !isBasicLevel && !isPersonal && (
									<Tab
										eventKey="competenceDetails"
										title={t('Competence', 'Osaaminen')}
										onEnter={() => setLoadCompetences(true)}
									>
										{loadCompetences && (
											<>
												<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
												<Form.Group as={Col} xl={4} controlId="education">
													<Form.Label>{t('EducationToSalaries', 'Koulutus-tieto palkanlaskentaan')}</Form.Label>
													<Form.Control
														onChange={handleDetails}
														defaultValue={person.details?.education}
														isInvalid={!!formErrors.education}
														maxLength={fieldParams.education?.maxLength}
													/>
													<Form.Control.Feedback type="invalid">{formErrors.education}</Form.Control.Feedback>
												</Form.Group>
												<Accordion defaultActiveKey={['competence-1']} flush alwaysOpen>
													<Accordion.Item key="competence-1" eventKey="competence-1">
														<Accordion.Header>{t('Educations', 'Tutkinnot')}</Accordion.Header>
														<Accordion.Body className="d-flex flex-column h-450">
															<EducationView person={person} />
														</Accordion.Body>
													</Accordion.Item>
													<Accordion.Item key="competence-2" eventKey="competence-2">
														<Accordion.Header>{t('OtherEducation', 'Muu koulutus')}</Accordion.Header>
														<Accordion.Body className="d-flex flex-column h-450">
															<CourseView person={person} />
														</Accordion.Body>
													</Accordion.Item>
													<Accordion.Item key="competence-3" eventKey="competence-3">
														<Accordion.Header>{t('Skills', 'Taidot')}</Accordion.Header>
														<Accordion.Body className="d-flex flex-column h-450">
															<SkillsView person={person} />
														</Accordion.Body>
													</Accordion.Item>
													<Accordion.Item key="competence-4" eventKey="competence-4">
														<Accordion.Header>{t('Languages', 'Kielitaito')}</Accordion.Header>
														<Accordion.Body className="d-flex flex-column h-450">
															<LanguagesView person={person} />
														</Accordion.Body>
													</Accordion.Item>
												</Accordion>
											</>
										)}
									</Tab>
								)}

								{person.personId && auth.discussions && !isBasicLevel && !isPersonal && (
									<Tab
										eventKey="disucssions"
										title={t('Discussions', 'Keskustelut')}
										onEnter={() => setLoadDiscussions(true)}
									>
										{loadDiscussions && (
											<>
												<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
												<Row className="h-450">
													<DiscussionView person={person} />
												</Row>
											</>
										)}
									</Tab>
								)}

								{(auth.personAttachments || isPersonal) && (
									<Tab eventKey="attachments" title={t('Attachments', 'Liitteet')}>
										<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
										<AttachmentView
											section="person"
											entityid={person.personId}
											attachments={person.attachments}
											savedId={
												(actionType === 'POST_PERSON_SUCCEEDED' || actionType === 'POST_PERSON_EDIT_SUCCEEDED') &&
												person.personId
											}
											isPersonal={isPersonal}
										/>

										{person.contractAttachments?.result?.length > 0 && (
											<Accordion defaultActiveKey={['contractAttachments']} flush alwaysOpen>
												<Accordion.Item key="contractAttachments" eventKey="contractAttachments">
													<Accordion.Header>{`${t('Contract', 'Työsopimus')} ${t('Attachments', 'Liitteet')}`}</Accordion.Header>
													<Accordion.Body className="d-flex flex-column h-450">
														<AttachmentView
															section="contract"
															attachments={person.contractAttachments}
															readOnly
															isPersonal={isPersonal}
														/>
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										)}
									</Tab>
								)}

								{(auth.salary || isPersonal) && (
									<Tab eventKey="personMemo" title={t('Memo', 'Muistio')}>
										<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
										<PersonalMemos person={person} handleOtherDetails={handleOtherDetails} isPersonal={isPersonal} />
									</Tab>
								)}
								{!isPersonal && (
									<Tab eventKey="personSettings" title={t('Settings', 'Asetukset')}>
										<EmploymentEndBanner endDate={person.employmentDetails?.endDate} />
										<PersonSettings person={person} setPerson={setPerson} />
									</Tab>
								)}
							</Tabs>
						</Modal.Body>
					</FormProvider>

					<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button testId="cancel" variant="cancel" onClick={handleClose} disabled={submitForm}>
							{t('Cancel', 'Peruuta')}
						</Button>
						<div style={{ display: 'flex', gap: '8px' }}>
							{person.employmentDetails?.startDate && !person.employmentDetails?.endDate && !isPersonal && (
								<Button
									testId="end-employment"
									className="endEmployment"
									variant="error"
									onClick={handleEmploymentEnd}
									disabled={submitForm}
								>
									{t('EndEmployment', 'Päätä työsuhde')}
								</Button>
							)}
							{!isPersonal && person.employmentDetails?.startDate && person.employmentDetails?.endDate && (
								<Button
									testId="open-employment"
									className="openEmployment"
									variant="primary"
									onClick={handleEmploymentOpen}
									disabled={submitForm}
								>
									{t('OpenEmployment', 'Aloita työsuhde uudelleen')}
								</Button>
							)}
							<Button testId="save" variant="primary" onClick={handleSubmit} disabled={submitForm}>
								{submitForm && <LoadingButton />}
								{t('Save', 'Tallenna')}
							</Button>
						</div>
					</Modal.Footer>
				</Modal>
			)}

			<IntegrationWarningModal
				show={showIntegrationConfirm}
				onHide={() => setShowIntegrationConfirm(false)}
				onConfirm={executeFinalSave}
				integrationErrors={integrationErrors}
			/>
			<ProcountorVersionWarningModal
				show={showProcountorVersionWarning}
				onHide={() => {
					setShowProcountorVersionWarning(false)
					setSubmitForm(false)
				}}
				onConfirm={continueAfterProcountorVersionCheck}
			/>
			<DuplicateSocSecModal
				show={socSecDuplicateWarning}
				onHide={() => setSocSecDuplicateWarning(false)}
				onConfirm={() => {
					setSocSecDuplicateWarning(false)
					proceedToSave()
				}}
			/>
		</>
	)
}
